import { useQuery } from "@tanstack/react-query";
import { FilterOutline30Icon } from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import MapAlert from "features/CenterContent/RoleContent/VehicleMap/MapAlert";
import { Wrapper } from "features/CenterContent/RoleContent/VehicleMap/VehicleMap";
import VehicleMapLayers from "features/CenterContent/RoleContent/VehicleMap/VehicleMapLayers";
import {
  THIRTY_SECONDS_IN_MS,
  VehicleType,
  vehicleTypes,
} from "features/CenterContent/RoleContent/VehicleMap/constants";
import { getFilteredVehicles } from "features/CenterContent/RoleContent/VehicleMap/utils";
import {
  TrainGpsResponse,
  TrainGpsResponseSchema,
} from "features/CenterContent/shared/types";
import { trainGpsDataToBaseGpsData } from "features/CenterContent/shared/utils";
import { useState } from "react";
import {
  CardSelect,
  CardSelectContent,
  CardSelectTrigger,
  Checkbox,
  Field,
  Flex,
  Grid,
  GridItem,
  SearchInput,
  Separator,
  SimpleGrid,
  Switch,
  Text,
} from "shared/components/Spor";
import useDebouncedValue from "shared/hooks/useDebouncedValue";
import { toggle } from "shared/utils/objectUtils";
import { useTheme } from "styled-components";

const VehicleMapTrain = () => {
  const [showOldVehicleTypes, setShowOldVehicleTypes] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, 500);
  const [currentFilters, setCurrentFilters] = useState<string[]>([]);

  const { data: trainGps, isError } = useQuery({
    queryKey: ["trainGps"],
    refetchInterval: THIRTY_SECONDS_IN_MS,
    queryFn: ({ signal }) =>
      axiosClient
        .get<TrainGpsResponse>(
          `${getBackendUrl()}/trainGps/gpsDataForVehicleSets/NO`,
          {
            signal,
          },
        )
        .then((res) => TrainGpsResponseSchema.parse(res.data)),
  });

  const filterdVehicles =
    getFilteredVehicles(trainGps, query, currentFilters, showOldVehicleTypes) ??
    [];

  const hasNoResults: boolean =
    !filterdVehicles?.some((vehicle) =>
      vehicle.resourceId.startsWith(debouncedQuery),
    ) && debouncedQuery.length !== 0;

  const numberOfResults = (type: VehicleType) => {
    if (type !== "Vogn") {
      return (
        filterdVehicles?.filter((vehicle) =>
          vehicle.resourceId.startsWith(type),
        ).length || 0
      );
    }
    return (
      filterdVehicles?.filter(
        (vehicle) =>
          vehicle.resourceId.startsWith("2") ||
          vehicle.resourceId.startsWith("5-") ||
          vehicle.resourceId.startsWith("7-"),
      ).length || 0
    );
  };

  const theme = useTheme();

  return (
    <Wrapper
      position="relative"
      height="100%"
      width="100%"
      align="center"
      justify="center"
    >
      <Grid
        position="absolute"
        alignItems="center"
        column={2}
        top={8}
        left={0}
        p="12px"
        gap="12px"
        zIndex={1000}
      >
        <SearchInput
          label="Søk på sett-ID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onReset={() => setQuery("")}
          background={theme.colorBackgroundMain}
        />
        <CardSelect>
          <CardSelectTrigger
            icon={<FilterOutline30Icon />}
            bg={theme.colorBackgroundMain}
          >
            Filtrér ({currentFilters.length})
          </CardSelectTrigger>
          <CardSelectContent
            border="none"
            width="300px"
            background={theme.colorBackgroundMain}
            boxShadow={theme.boxShadow}
          >
            <Flex justifyContent="center" p="12px">
              <SimpleGrid columns={2} role="group">
                {vehicleTypes.map((type) => (
                  <Checkbox
                    key={type}
                    value={type}
                    padding="12px"
                    checked={currentFilters.includes(type)}
                    onCheckedChange={() =>
                      setCurrentFilters((prev) => toggle(prev, type))
                    }
                  >
                    {type}{" "}
                    <Text color={theme.colorTextSecondary} display="inline">
                      ({numberOfResults(type)})
                    </Text>
                  </Checkbox>
                ))}
                <Flex
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  gridColumn="span 2"
                >
                  <Separator my="12px" />
                  <Flex
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Field>Vis eldre enn 1 døgn</Field>
                    <Switch
                      size="sm"
                      checked={showOldVehicleTypes}
                      onChange={() =>
                        setShowOldVehicleTypes(!showOldVehicleTypes)
                      }
                    />
                  </Flex>
                </Flex>
              </SimpleGrid>
            </Flex>
          </CardSelectContent>
        </CardSelect>
        <GridItem colSpan={2}>
          <MapAlert
            isError={isError}
            hasNoResults={hasNoResults}
            query={debouncedQuery}
          />
        </GridItem>
      </Grid>

      <VehicleMapLayers
        gpsData={trainGpsDataToBaseGpsData(filterdVehicles)}
        query={debouncedQuery}
      />
    </Wrapper>
  );
};

export default withErrorBoundary(VehicleMapTrain);
